/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { apiClient } from '../utils/apiClient';

export interface PlatformConfig {
  loaded: boolean;
  hideUnfinishedFeatures: boolean;
  supportedNetworks: string[];
  supportedTradingPairs: string[];
  enablePaperTradingDefault: boolean;
}

const PERMISSIVE_DEFAULTS: PlatformConfig = {
  loaded: false,
  hideUnfinishedFeatures: false,
  supportedNetworks: [],
  supportedTradingPairs: [],
  enablePaperTradingDefault: false,
};

const PlatformConfigContext = createContext<PlatformConfig>(PERMISSIVE_DEFAULTS);

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map((item) => String(item));
};

export const PlatformConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<PlatformConfig>(PERMISSIVE_DEFAULTS);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data: any = await apiClient.get('/public-config', { silent: true });
        if (cancelled) return;
        setConfig({
          loaded: true,
          hideUnfinishedFeatures: Boolean(data?.hide_unfinished_features),
          supportedNetworks: toStringArray(data?.supported_networks),
          supportedTradingPairs: toStringArray(data?.supported_trading_pairs),
          enablePaperTradingDefault: Boolean(data?.enable_paper_trading_default),
        });
      } catch {
        if (cancelled) return;
        // Permissive fallback: still mark loaded so consumers stop showing
        // skeletons and render with no platform-side restrictions applied.
        setConfig({ ...PERMISSIVE_DEFAULTS, loaded: true });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PlatformConfigContext.Provider value={config}>
      {children}
    </PlatformConfigContext.Provider>
  );
};

export const usePlatformConfig = (): PlatformConfig =>
  useContext(PlatformConfigContext);
